import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text } from '@repletoryreach/sdk-ui';
import { EntityCardGrid, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate, formatInteger } from '../../../lib/format';
import { TenantAvatar } from './TenantAvatar';
import { useT } from '../../../i18n/I18nProvider';

type Tenant = NonNullable<Awaited<ReturnType<typeof admin.listTenants>>['data']>[number];

export function TenantsGrid({ records }: { records: Tenant[] }) {
  const navigate = useNavigate();
  const t = useT();
  return (
    <EntityCardGrid<Tenant>
      records={records}
      idAccessor="id"
      cols={{ base: 1, sm: 2, lg: 3 }}
      spacing="md"
      onCardClick={(row) => navigate(`/tenants/${row.id}`)}
      renderCard={(tenant) => (
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap="sm" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
              <TenantAvatar name={tenant.name} size="md" />
              <Stack gap="xs" style={{ minWidth: 0 }}>
                <Text role="subtitle" fw={600} truncate>{tenant.name}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)"><code>{tenant.slug}</code></Text>
              </Stack>
            </Group>
            <StatusBadge size="xs" status={tenant.is_active ? 'healthy' : 'inactive'}>
              {tenant.is_active ? t('status.active') : t('status.inactive')}
            </StatusBadge>
          </Group>
          <Group gap="md" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
            <Stat label={t('tenants.columns.country')} value={tenant.country_iso} />
            {/* The table shows the full IANA zone, so the card does too: one field, one convention. */}
            <Stat label={t('tenants.columns.timezone')} value={<span title={tenant.timezone}>{tenant.timezone}</span>} />
            <Stat label={t('tenants.columns.users')} value={formatInteger(tenant.user_count)} />
          </Group>
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('tenants.createdAt', { date: formatDate(tenant.created_at) })}
          </Text>
        </Stack>
      )}
    />
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack gap="xs" style={{ minWidth: 0 }}>
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <Text role="bodySmall" fw={600} truncate>{value}</Text>
    </Stack>
  );
}
